/**
 * Eager Theme Loader
 *
 * This module runs IMMEDIATELY when imported, before any CSS is loaded.
 * It applies cached theme from sessionStorage to prevent FOUC (Flash of Unstyled Content).
 *
 * IMPORTANT: This file must be imported BEFORE any CSS files in main.tsx.
 */

import { Theme } from '@/types/theme';
import { generateColorVariants, getContrastTextColor } from '@/utils/colorUtils';

const THEME_CACHE_KEY = 'tenant_theme_cache';
const THEME_CACHE_TTL = 3600000; // 1 hour

/**
 * Apply theme to document by injecting CSS variables
 * (Duplicated from ThemeContext to avoid circular dependencies)
 */
const applyThemeToDocument = (theme: Theme) => {
  const root = document.documentElement;

  // Core colors
  root.style.setProperty('--color-primary', theme.primary_color);
  root.style.setProperty('--color-secondary', theme.secondary_color);

  const primaryVariants = generateColorVariants(theme.primary_color);
  root.style.setProperty('--color-primary-hover', primaryVariants.hover);
  root.style.setProperty('--color-primary-light', primaryVariants.light);
  root.style.setProperty('--color-primary-dark', primaryVariants.dark);

  const primaryTextColor = getContrastTextColor(theme.primary_color);
  root.style.setProperty('--color-primary-text', primaryTextColor);

  const secondaryVariants = generateColorVariants(theme.secondary_color);
  root.style.setProperty('--color-secondary-hover', secondaryVariants.hover);
  root.style.setProperty('--color-secondary-light', secondaryVariants.light);

  root.style.setProperty('--color-success', theme.colors.success);
  root.style.setProperty('--color-success-hover', theme.colors.success_hover);
  root.style.setProperty('--color-warning', theme.colors.warning);
  root.style.setProperty('--color-warning-hover', theme.colors.warning_hover);
  root.style.setProperty('--color-danger', theme.colors.danger);
  root.style.setProperty('--color-danger-hover', theme.colors.danger_hover);
  root.style.setProperty('--color-info', theme.colors.info);
  root.style.setProperty('--color-info-hover', theme.colors.info_hover);

  root.style.setProperty('--color-text-primary', theme.colors.text_primary);
  root.style.setProperty('--color-text-secondary', theme.colors.text_secondary);
  root.style.setProperty('--color-text-muted', theme.colors.text_muted);

  root.style.setProperty('--color-bg-primary', theme.colors.background_primary);
  root.style.setProperty('--color-bg-secondary', theme.colors.background_secondary);
  root.style.setProperty('--color-bg-tertiary', theme.colors.background_tertiary);

  // Subtle background
  const rgb = theme.primary_color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (rgb) {
    const r = parseInt(rgb[1], 16);
    const g = parseInt(rgb[2], 16);
    const b = parseInt(rgb[3], 16);
    const factor = 0.95;
    const subtleR = Math.round(r + (255 - r) * factor);
    const subtleG = Math.round(g + (255 - g) * factor);
    const subtleB = Math.round(b + (255 - b) * factor);
    const subtleColor = `#${[subtleR, subtleG, subtleB].map(x => x.toString(16).padStart(2, '0')).join('')}`;
    root.style.setProperty('--color-background-subtle', subtleColor);
  } else {
    root.style.setProperty('--color-background-subtle', primaryVariants.light);
  }

  root.style.setProperty('--color-border', theme.colors.border_color);
  root.style.setProperty('--color-border-focus', theme.colors.border_focus);

  // Typography
  root.style.setProperty('--font-primary', theme.font_primary);
  root.style.setProperty('--font-heading', theme.font_heading);
  root.style.setProperty('--font-mono', theme.typography.font_mono);

  root.style.setProperty('--font-size-xs', theme.typography.font_size_xs);
  root.style.setProperty('--font-size-sm', theme.typography.font_size_sm);
  root.style.setProperty('--font-size-base', theme.typography.font_size_base);
  root.style.setProperty('--font-size-lg', theme.typography.font_size_lg);
  root.style.setProperty('--font-size-xl', theme.typography.font_size_xl);
  root.style.setProperty('--font-size-2xl', theme.typography.font_size_2xl);
  root.style.setProperty('--font-size-3xl', theme.typography.font_size_3xl);

  root.style.setProperty('--font-weight-normal', theme.typography.font_weight_normal);
  root.style.setProperty('--font-weight-medium', theme.typography.font_weight_medium);
  root.style.setProperty('--font-weight-semibold', theme.typography.font_weight_semibold);
  root.style.setProperty('--font-weight-bold', theme.typography.font_weight_bold);

  // Spacing
  root.style.setProperty('--spacing-xs', theme.spacing.xs);
  root.style.setProperty('--spacing-sm', theme.spacing.sm);
  root.style.setProperty('--spacing-md', theme.spacing.md);
  root.style.setProperty('--spacing-lg', theme.spacing.lg);
  root.style.setProperty('--spacing-xl', theme.spacing.xl);
  root.style.setProperty('--spacing-2xl', theme.spacing['2xl']);

  // Border radius
  root.style.setProperty('--radius-sm', theme.border_radius.sm);
  root.style.setProperty('--radius-md', theme.border_radius.md);
  root.style.setProperty('--radius-lg', theme.border_radius.lg);
  root.style.setProperty('--radius-xl', theme.border_radius.xl);
  root.style.setProperty('--radius-full', theme.border_radius.full);

  // Shadows
  root.style.setProperty('--shadow-sm', theme.shadows.sm);
  root.style.setProperty('--shadow-md', theme.shadows.md);
  root.style.setProperty('--shadow-lg', theme.shadows.lg);
  root.style.setProperty('--shadow-xl', theme.shadows.xl);

  // Transitions
  root.style.setProperty('--transition-fast', theme.transitions.fast);
  root.style.setProperty('--transition-base', theme.transitions.base);
  root.style.setProperty('--transition-slow', theme.transitions.slow);

  // Apply primary font to body
  if (document.body) {
    document.body.style.fontFamily = theme.font_primary;
  }
};

// IIFE: Apply cached theme immediately when this module loads
(() => {
  try {
    const cachedData = sessionStorage.getItem(THEME_CACHE_KEY);
    if (cachedData) {
      const { theme: cachedTheme, timestamp } = JSON.parse(cachedData);
      const now = Date.now();

      if (now - timestamp < THEME_CACHE_TTL) {
        applyThemeToDocument(cachedTheme);

        // DEBUG: Watch for CSS variable changes
        if (typeof MutationObserver !== 'undefined') {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                const primary = document.documentElement.style.getPropertyValue('--color-primary');
                if (!primary || primary === '') {
                  // Re-apply theme immediately
                  applyThemeToDocument(cachedTheme);
                }
              }
            });
          });

          observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['style'],
          });
        }
      }
    }
  } catch (error) {
    console.warn('⚠️ Failed to apply cached theme eagerly:', error);
  }
})();
